import { createUniqueId, type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import { controlKey } from '../../lib/card-controls';
import { useCardControls } from '../../lib/card-controls-context';
import { epicModelName, epicModelOptions } from '../../lib/epic-model';
import { isSaving } from '../../lib/flood-guard-context';
import type { CardEpicRow, CardState } from '../../lib/machine-card';
import { Select } from '../Select';

// The epic's model (#6753 over #7012, prototype modelPick `:2346`): for an admin on this
// org's machine, a `Select` labelled "AI model for epic <title>" whose value is the epic's
// STORED `llm` and whose options are the account's keys' offers for `general`
// (`lib/epic-model.ts`); for anyone else, the stored model as a read-only chip. "Automatic"
// when `llm` is null (deviation 5). An epic whose `llm` field the api did not report shows
// neither, and neither shows on an offline machine (the prototype hides the chip there).
// A pick waits 700 ms in the page's flood guard; on its answer the row shows "From the next
// slice" for 4 s.

export function EpicModelPicker(props: {
  readonly epic: CardEpicRow;
  readonly state: CardState;
}): JSX.Element {
  const handle = useCardControls();
  const id = createUniqueId();
  const reported = () => props.epic.llm !== undefined && props.state !== 'offline';
  const picker = () => (handle?.on() === true && handle.keys() !== undefined ? handle : null);
  const chip = (): JSX.Element => (
    <span class="mission-ctl__chip" title={t('fleet.machines.ctl_model_chip_title')}>
      {epicModelName(props.epic.llm ?? null)}
    </span>
  );
  return (
    <Show when={reported()}>
      <Show when={picker()} fallback={chip()}>
        {(h) => {
          const llm = () => h().epics.storedLlm(props.epic) ?? null;
          const saving = () => isSaving(h().card.page, controlKey.epicModel(props.epic.plan_id));
          const label = () => t('fleet.machines.ctl_model_label', { title: props.epic.title });
          return (
            <span class="mission-ctl__model" title={t('fleet.machines.ctl_model_title')}>
              <label class="sr-only" for={id}>
                {label()}
              </label>
              <Select
                a11y={{ id }}
                label={label()}
                value={h().epics.modelValue(props.epic) ?? ''}
                options={epicModelOptions(h().keys() ?? [], llm())}
                disabled={saving()}
                onChange={(v) => h().epics.pickModel(props.epic, v)}
              />
              <Show when={saving()}>
                <span class="mission-ctl__saving">{t('fleet.machines.ctl_saving')}</span>
              </Show>
              <Show when={h().epics.modelNote(props.epic.plan_id)}>
                <span class="mission-ctl__saving">{t('fleet.machines.ctl_model_next')}</span>
              </Show>
            </span>
          );
        }}
      </Show>
    </Show>
  );
}
